import TomSelect from "tom-select/base";
import clearButtonPlugin from "tom-select/plugins/clear_button/plugin.js";
import removeButtonPlugin from "tom-select/plugins/remove_button/plugin.js";
import dropdownInputPlugin from "tom-select/plugins/dropdown_input/plugin.js";

TomSelect.define("clear_button", clearButtonPlugin);
TomSelect.define("remove_button", removeButtonPlugin);
TomSelect.define("dropdown_input", dropdownInputPlugin);

// select2's data shape ({ id, text }) is kept as-is on the Rails side
// (see CustomersHelper#addresses_collection and friends) so no backend
// changes are needed for the field mapping below.
export const TomSelectConfig = {
  create: false,
  // clear_button: an "x" clearing the whole field at once (single and
  // multi mode). remove_button: a per-item "x" on each multi-mode chip so
  // one driver can be removed without clearing the others. dropdown_input:
  // moves the search box out of the control and into the top of the
  // dropdown list, instead of squeezed next to the selected value(s).
  plugins: ["clear_button", "remove_button", "dropdown_input"],
  valueField: "id",
  labelField: "text",
  searchField: "text",
};

// Initializes (or returns the existing instance of) a Tom Select on the
// given <select> element. Dispatches a "tomselect:change" CustomEvent
// (detail: { value }) on the original element whenever the selection
// changes to a non-empty value -- listen for that instead of the old
// jQuery-delegated "select2:select" event. Using a plain native event
// (rather than an init-time callback) means any number of independent
// controllers can listen on the same element regardless of which one
// happened to call initTomSelect first.
export function initTomSelect(selectEl) {
  if (!selectEl) return undefined;
  if (selectEl.tomselect) return selectEl.tomselect;

  // Fall back to the <select>'s own blank option text (Rails' `prompt:`/
  // `include_blank:`) when no explicit data-placeholder was set -- covers
  // e.g. a date_select's day/month/year triplet, where Rails only lets you
  // pass one html_options hash for all three, so they can't each get their
  // own data-placeholder attribute.
  const placeholder =
    selectEl.dataset.placeholder ?? selectEl.querySelector('option[value=""]')?.textContent;

  return new TomSelect(selectEl, {
    ...TomSelectConfig,
    placeholder,
    onItemAdd(value) {
      if (value === "") return;
      selectEl.dispatchEvent(
        new CustomEvent("tomselect:change", { detail: { value }, bubbles: true }),
      );
    },
  });
}

export function destroyTomSelect(selectEl) {
  if (selectEl?.tomselect) {
    selectEl.tomselect.destroy();
  }
}

// Replaces a Tom Select's options with newOptions (array of {id, text}),
// then selects selectedId. Mirrors the old refreshAddressField/
// refreshCustomerField select2 helpers -- same call sites, same data
// shape, just driven through the Tom Select instance API.
export function refreshTomSelectOptions(selectEl, newOptions, selectedId) {
  const instance = selectEl?.tomselect;
  if (!instance) return;

  instance.clear(true);
  instance.clearOptions();
  instance.addOptions(newOptions);
  instance.setValue(selectedId ?? "", true);
  selectEl.dispatchEvent(new Event("change", { bubbles: true }));
}
